import React, { useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { injectIntl, FormattedMessage } from 'react-intl';
import { createForm } from 'rc-form';
// eslint-disable-next-line import/no-extraneous-dependencies
import get from 'lodash/get';
import uuid from 'uuid/v4';
import { Grid, Typography, Button, Fab } from '@material-ui/core';
import AddSharp from '@material-ui/icons/AddSharp';
import SearchIcon from '@material-ui/icons/Search';

import { updateDetailSettings } from 'Console/data/applicationDetail/settings/SettingsAction';

import {
    selectApplicationSettingsData,
    selectApplicationJourneyTracker,
} from 'Console/data/applicationDetail/settings/SettingsSelector';

import DrawerWrapper from 'Console/components/common/drawer/DrawerWrapper';
import Search from 'Console/components/common/search/Search';
import Breadcrumb from 'Console/components/common/Breadcrumb';
import JourneyList from 'Console/components/applications/detail/journeys/JourneyList';
import CreateNewJourney from 'Console/components/applications/detail/journeys/CreateNewJourney';
import ConfirmDialog from 'Console/components/common/confirmDialog/ConfirmDialog';

import { handleDrawerWrapperToggle } from 'Console/data/app/actions';
import { selectContentLanguageCode } from 'Console/data/language/selectors';
import { selectIsDrawerWrapperShown, selectContentLanguage } from 'Console/data/app/selectors';
import { ROUTES } from 'Console/constants/routes';

const styles = (theme) => ({
    root: {
        flexGrow: 1,
        alignItems: 'center',
        padding: '0 24px',
    },
    formWrapper: {
        width: '80%',
        margin: 'auto',
    },
    header: {
        paddingBottom: theme.typography.pxToRem(28),
        [theme.breakpoints.down('xs')]: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
        },
    },
    button: {
        [theme.breakpoints.down('xs')]: {
            marginRight: 0,
            marginTop: theme.typography.pxToRem(15),
        },
    },
});

const ApplicationJourneys = ({
    classes,
    intl,
    intl: { formatMessage },
    form,
    form: { getFieldsValue, setFieldsValue },
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [journeyToEdit, setJourneyToEdit] = useState(null);
    const [journeyToDelete, setJourneyToDelete] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    const dispatch = useDispatch();

    const isDrawerWrapperShown = useSelector(selectIsDrawerWrapperShown);
    const applicationSettingsData = useSelector(selectApplicationSettingsData);
    const journeyTracker = useSelector(selectApplicationJourneyTracker);
    const contentLanguageCode = useSelector(selectContentLanguageCode);
    const contentLanguage = useSelector(selectContentLanguage);

    const openDrawerWrapper = useCallback(() => {
        dispatch(handleDrawerWrapperToggle(true));
    }, [dispatch]);

    const handleJourneysSearch = useCallback((event) => {
        const { value } = event.target;
        setSearchTerm(value);
    }, []);

    const closeDrawerWrapper = useCallback(() => {
        setJourneyToEdit(null);
        setFieldsValue({
            name: '',
            afterJourneyComplete: 'noAction',
            afterTourInJourneyComplete: 'openContextHelp',
        });
        dispatch(handleDrawerWrapperToggle(false));
    }, [dispatch, setFieldsValue]);

    const handleJourneyClick = useCallback(
        (journey) => () => {
            setJourneyToEdit(journey);
            dispatch(handleDrawerWrapperToggle(true));
        },
        [dispatch]
    );

    const handleCreateOrUpdateJourney = useCallback(() => {
        const { name, afterJourneyComplete, afterTourInJourneyComplete } = getFieldsValue([
            'name',
            'afterJourneyComplete',
            'afterTourInJourneyComplete',
        ]);

        // To understand these structure pls inspect in network tab
        // applications endpoint - and look application detail
        // metadata.journeyTracker[identifier] ...
        if (journeyToEdit) {
            // UPDATE EXISTING JOURNEY
            dispatch(
                updateDetailSettings(
                    {
                        [`metadata.journeyTracker.${journeyToEdit.content.id}`]: {
                            content: {
                                id: journeyToEdit.content.id,
                                name,
                            },
                            order: get(`metadata.journeyTracker.${journeyToEdit.content.id}.order`) || 0,
                            enabledInContextHelp:
                                get(
                                    `metadata.journeyTracker.${journeyToEdit.content.id}.enabledInContextHelp`
                                ) || false,
                            actions: {
                                afterJourneyComplete,
                                afterTourInJourneyComplete,
                            },
                        },
                    },
                    {
                        success: (
                            <FormattedMessage
                                id="Notification.update.journey.success"
                                defaultMessage="Your journey was updated"
                            />
                        ),
                        fail: (
                            <FormattedMessage
                                id="Notification.update.journey.fail"
                                defaultMessage="Your journey was not updated, something is wrong"
                            />
                        ),
                    }
                )
            );
        } else {
            // CREATE NEW JOURNEY
            const createIdentifier = uuid();
            dispatch(
                updateDetailSettings(
                    {
                        'metadata.journeyTracker': {
                            ...journeyTracker,
                            [createIdentifier]: {
                                content: {
                                    id: createIdentifier,
                                    name,
                                },
                                enabledInContextHelp: false,
                                order: 0,
                                actions: {
                                    afterJourneyComplete,
                                    afterTourInJourneyComplete,
                                },
                            },
                        },
                    },
                    {
                        success: (
                            <FormattedMessage
                                id="Your journey was created"
                                defaultMessage="Your journey was created"
                            />
                        ),
                        fail: (
                            <FormattedMessage
                                id="Notification.create.journey.fail"
                                defaultMessage="Your journey was not created, something is wrong"
                            />
                        ),
                    }
                )
            );
        }

        setFieldsValue({
            name: '',
            afterJourneyComplete: 'noAction',
            afterTourInJourneyComplete: 'openContextHelp',
        });
        closeDrawerWrapper();
    }, [dispatch, closeDrawerWrapper, journeyToEdit, getFieldsValue, journeyTracker, setFieldsValue]);

    const handleDeleteJourney = useCallback(
        (identifier) => () => {
            setDeleteDialogOpen(false);

            const updatedJourneyTracker = {
                ...journeyTracker,
            };

            delete updatedJourneyTracker[identifier];

            dispatch(
                updateDetailSettings(
                    {
                        'metadata.journeyTracker': updatedJourneyTracker,
                    },
                    {
                        success: (
                            <FormattedMessage
                                id="Notification.delete.journey.success"
                                defaultMessage="Your journey was deleted"
                            />
                        ),
                        fail: (
                            <FormattedMessage
                                id="Notification.update.journey.fail'"
                                defaultMessage="Your journey was not updated, something is wrong"
                            />
                        ),
                    }
                )
            );
        },
        [dispatch, journeyTracker]
    );

    const handleDeleteJourneyConfirmDialog = useCallback(
        (identifier) => () => {
            setDeleteDialogOpen(true);
            setJourneyToDelete(identifier);
        },
        []
    );

    const journeyList = journeyTracker ? Object.values(journeyTracker) : [];

    const filterJourney = (item, searchTerm) => {
        if (!item) {
            return false;
        }

        return item.toLowerCase().indexOf(searchTerm.toLowerCase()) > -1;
    };

    const filteredJourneyList = searchTerm
        ? journeyList.filter(
              (journey) =>
                  filterJourney(journey.content.id, searchTerm) ||
                  filterJourney(journey.content.name[contentLanguageCode], searchTerm)
          )
        : journeyList;

    const values = getFieldsValue(['name']);

    return (
        <Grid container className={classes.root}>
            <Typography variant="h6">
                <Grid item xs={12}>
                    <Breadcrumb
                        paths={[
                            {
                                to: ROUTES.APPLICATIONS,
                                text: formatMessage({ id: 'Navigation.applications' }),
                            },
                            {
                                text: applicationSettingsData.name,
                            },
                        ]}
                    />
                </Grid>
            </Typography>

            <Grid>
                <DrawerWrapper
                    title={formatMessage({
                        id: journeyToEdit ? 'Journeys.edit.journey' : 'Journeys.create.journey',
                    })}
                    name={formatMessage({ id: 'Buttons.cancel' })}
                    term={formatMessage({ id: journeyToEdit ? 'Buttons.edit' : 'Buttons.create' })}
                    isShown={isDrawerWrapperShown}
                    onClose={closeDrawerWrapper}
                    onConfirm={handleCreateOrUpdateJourney}
                    confirmDisabled={!values.name}
                >
                    <Grid container className={classes.formWrapper}>
                        <CreateNewJourney form={form} intl={intl} journeyToEdit={journeyToEdit} />
                    </Grid>
                </DrawerWrapper>
            </Grid>

            <Grid item xs={12}>
                <Grid container spacing={24} justify="center" alignItems="flex-end">
                    <Grid item xs={12}>
                        <Grid
                            container
                            justify="space-between"
                            alignItems="flex-end"
                            className={classes.header}
                        >
                            <Grid item>
                                <Typography variant="h6">
                                    <FormattedMessage
                                        id="Applications.navigation.detail.journeys"
                                        defaultMessage="Journeys"
                                    />
                                </Typography>
                            </Grid>

                            <Grid item>
                                <Typography className={classes.button}>
                                    <Button
                                        id="yeself-button"
                                        variant="contained"
                                        color="primary"
                                        onClick={openDrawerWrapper}
                                    >
                                        <AddSharp fontSize='small'/>
                                        <FormattedMessage
                                            id="Buttons.createJourney"
                                            defaultMessage="Create journey"
                                        />
                                    </Button>
                                </Typography>
                            </Grid>
                        </Grid>
                        <Grid container justify="flex-start">
                            <Grid style={{ width: '12rem' }}>
                                <Search
                                    id="form-item-search"
                                    placeholder={formatMessage({
                                        id: 'Journeys.search.placeholder',
                                    })}
                                    onChange={handleJourneysSearch}
                                />
                            </Grid>
                            <Fab color="primary" aria-label="Search" style={{ marginLeft: '0.9375rem' }}>
                                <SearchIcon />
                            </Fab>
                        </Grid>
                    </Grid>

                    <Grid item xs={12}>
                        <JourneyList
                            list={filteredJourneyList}
                            contentLanguage={contentLanguage}
                            onRowClick={handleJourneyClick}
                            deleteJourney={handleDeleteJourneyConfirmDialog}
                        />
                    </Grid>
                </Grid>
            </Grid>

            <ConfirmDialog
                title={formatMessage({ id: 'Journey.confirm.delete.dialog' })}
                open={deleteDialogOpen}
                content=""
                closeAction={() => {
                    setDeleteDialogOpen(false);
                    setJourneyToDelete(null);
                }}
                action={handleDeleteJourney(journeyToDelete)}
                agreeLabel={formatMessage({ id: 'Buttons.delete' })}
                disagreeLabel={formatMessage({ id: 'Buttons.cancel' })}
            />
        </Grid>
    );
};

export default withStyles(styles)(injectIntl(createForm()(ApplicationJourneys)));
